import { Camera, X, Check, Image as ImageIcon, Loader2, Database } from "lucide-react";
import { useCallback, useEffect, useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";

function photoCompress(file, maxKB = 500) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new window.Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        let { width, height } = img;
        const MAX = 1280;
        if (width > MAX || height > MAX) {
          const scale = MAX / Math.max(width, height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        let quality = 0.8;
        let dataUrl = canvas.toDataURL("image/jpeg", quality);
        while (dataUrl.length > maxKB * 1024 * 1.37 && quality > 0.3) {
          quality -= 0.1;
          dataUrl = canvas.toDataURL("image/jpeg", quality);
        }
        resolve(dataUrl);
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Capture une photo unique nommée (ex: "AV-Droit" ou "Permis - recto").
 *
 * iter226 — Deux sources au choix sur mobile : appareil photo OU galerie
 * (avant, l'attribut `capture` forçait l'appareil photo et empêchait de
 * reprendre une photo déjà présente dans le téléphone).
 */
export function PhotoCornerSlot({ label, value, onChange, testid }) {
  const [busy, setBusy] = useState(false);
  const [crmOpen, setCrmOpen] = useState(false);
  const camRef = useRef(null);
  const galRef = useRef(null);

  // iter267 — troisième source : la galerie Photos du CRM
  const prendreDuCrm = async (photoId) => {
    setCrmOpen(false);
    setBusy(true);
    try {
      const { data } = await api.post("/photos/data-urls", { photo_ids: [photoId] });
      onChange(data.items[0].data_url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Photo indisponible");
    } finally {
      setBusy(false);
    }
  };

  const handle = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";  // permet de re-sélectionner le même fichier
    if (!file) return;
    setBusy(true);
    try {
      const data = await photoCompress(file);
      onChange(data);
    } catch (err) {
      console.error(err);
    } finally {
      setBusy(false);
    }
  };

  const Source = ({ icon: Icon, text, onClick, tid }) => (
    <button
      type="button"
      onClick={onClick}
      className="flex-1 min-w-0 inline-flex items-center justify-center gap-1 px-1 py-1 rounded bg-white/95 border border-gray-200 text-[10px] font-semibold text-gray-700 hover:border-[#52a675] hover:text-[#2d5f3f] transition-colors"
      data-testid={tid}
    >
      <Icon className="w-3.5 h-3.5" /> {text}
    </button>
  );

  return (
    <div
      className={`relative block aspect-[4/3] rounded-md border-2 overflow-hidden ${
        value ? "border-solid border-[#52a675]/40" : "border-dashed border-gray-300"
      }`}
      data-testid={testid}
    >
      {value ? (
        <>
          <img src={value} alt={label} className="w-full h-full object-cover" />
          <div className="absolute top-1 right-1 bg-[#52a675] rounded-full p-1">
            <Check className="w-3 h-3 text-white" />
          </div>
          <button
            type="button"
            onClick={() => onChange(null)}
            className="absolute top-1 left-1 bg-red-500 hover:bg-red-600 text-white rounded-full p-1"
            data-testid={`${testid}-remove`}
          >
            <X className="w-3 h-3" />
          </button>
          <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-1 pt-0.5 pb-1">
            <div className="text-white text-[10px] font-semibold truncate text-center">{label}</div>
            <div className="flex gap-1 mt-0.5">
              <Source icon={Camera} text="Photo" onClick={() => camRef.current?.click()} tid={`${testid}-camera`} />
              <Source icon={ImageIcon} text="Galerie" onClick={() => galRef.current?.click()} tid={`${testid}-gallery`} />
              <Source icon={Database} text="CRM" onClick={() => setCrmOpen(true)} tid={`${testid}-crm`} />
            </div>
          </div>
        </>
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center p-2 text-center gap-1.5">
          <Camera className={`w-5 h-5 ${busy ? "animate-pulse text-[#52a675]" : "text-gray-400"}`} />
          <span className="text-xs font-semibold text-gray-700 leading-tight">{label}</span>
          {busy ? (
            <span className="text-[10px] text-gray-400">Compression…</span>
          ) : (
            <div className="flex gap-1 w-full px-1">
              <Source icon={Camera} text="Photo" onClick={() => camRef.current?.click()} tid={`${testid}-camera`} />
              <Source icon={ImageIcon} text="Galerie" onClick={() => galRef.current?.click()} tid={`${testid}-gallery`} />
              <Source icon={Database} text="CRM" onClick={() => setCrmOpen(true)} tid={`${testid}-crm`} />
            </div>
          )}
        </div>
      )}
      <input
        ref={camRef} type="file" accept="image/*" capture="environment"
        className="hidden" onChange={handle} data-testid={`${testid}-input-camera`}
      />
      <input
        ref={galRef} type="file" accept="image/*"
        className="hidden" onChange={handle} data-testid={`${testid}-input-gallery`}
      />
      <CrmPhotoPicker
        open={crmOpen}
        onClose={() => setCrmOpen(false)}
        onPick={prendreDuCrm}
        label={label}
        testid={testid}
      />
    </div>
  );
}

/**
 * iter267 — Sélecteur de photo dans la bibliothèque « Photos » du CRM.
 */
export function CrmPhotoPicker({ open, onClose, onPick, label, testid = "crm-photo" }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const { data } = await api.get("/photos");
      setItems(data.items || []);
    } finally {
      setLoading(false);
    }
  }, [open]);

  useEffect(() => { load(); }, [load]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid={`${testid}-crm-dialog`}>
        <DialogHeader>
          <DialogTitle>Photos du CRM</DialogTitle>
          <DialogDescription>
            Choisissez la photo à utiliser{label ? ` pour « ${label} »` : ""}.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-gray-400" /></div>
        ) : !items.length ? (
          <div className="py-10 text-center text-sm text-gray-500" data-testid={`${testid}-crm-empty`}>
            <ImageIcon className="w-8 h-8 mx-auto text-gray-300 mb-2" />
            Aucune photo dans la bibliothèque. Prenez-les depuis l&apos;onglet « Photos ».
          </div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-[55vh] overflow-y-auto content-start auto-rows-max" data-testid={`${testid}-crm-grid`}>
            {items.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => onPick(p.id)}
                className="rounded-md overflow-hidden border-2 border-gray-200 hover:border-[#52a675] transition-colors"
                data-testid={`${testid}-crm-item-${p.id}`}
              >
                <img src={p.thumb} alt="" className="w-full h-24 object-cover" />
                <div className="px-1 py-0.5 text-[10px] text-gray-600 truncate bg-white">
                  {p.plaque || "À classer"}
                </div>
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Grille des 4 coins (AV-D, AV-G, AR-D, AR-G). photos = { av_d, av_g, ar_d, ar_g }
 */
export default function PhotosCorners({ photos, onChange, testid = "photos-corners" }) {
  const update = (key, value) => onChange({ ...photos, [key]: value });
  return (
    <div className="grid grid-cols-2 gap-2 max-w-md" data-testid={testid}>
      <PhotoCornerSlot label="Avant gauche" value={photos?.av_g} onChange={(v) => update("av_g", v)} testid="photo-av-g" />
      <PhotoCornerSlot label="Avant droit" value={photos?.av_d} onChange={(v) => update("av_d", v)} testid="photo-av-d" />
      <PhotoCornerSlot label="Arrière gauche" value={photos?.ar_g} onChange={(v) => update("ar_g", v)} testid="photo-ar-g" />
      <PhotoCornerSlot label="Arrière droit" value={photos?.ar_d} onChange={(v) => update("ar_d", v)} testid="photo-ar-d" />
    </div>
  );
}

PhotosCorners.compress = photoCompress;
